import { createFeatureSelector, createSelector } from "@ngrx/store";
import { DiaryEventGroup } from "../models/diary-event-group";
import { DiaryEvent } from "../models/diary-event";
import { DiaryCalendarView } from "../models/diary-calendar-view";
import * as diaryReducers from "../reducers/diary-reducer"
import { DiaryEventFilterFieldProvider } from "../filter/diary-event-filter-field-provider"
import {RuleSet} from "ngx-angular-query-builder";
import * as widgetConfigSelectors from "../../widget-configs/selectors/widget-configs";
import { WidgetConfiguration } from "../../widget-configs/models/widget-configuration";
import * as filterSelectors from '../../common/filter/selectors/filter.selectors';
import * as filterReducers from '../../common/filter/reducers/filter.reducers';
import * as filterUtils from '../../common/filter/filter-utils';
import { StandardFilterStrategy } from "@common/filter/standard-filter-strategy";
import {FieldMetadata} from "@feature/common";

export const selectDiaryWidgetData = createFeatureSelector<diaryReducers.DiaryWidgetDataState>('diaryWidgetData');
/**
 * @todo: Should this and the adapter, currently in diary-reducer should be moved to a diary.adapter.ts?
 */
export const {
  selectEntities,
} = diaryReducers.diaryWidgetAdapter.getSelectors(selectDiaryWidgetData);

export const selectEntityByWidgetId = (widgetId: string) => createSelector(selectEntities, (entities) => entities[widgetId.toString()]);

export const getDiaryEventsLoading = (widgetId: string) => createSelector(selectEntityByWidgetId(widgetId.toString()),
  (entity: diaryReducers.DiaryWidgetInstance) => {
    return entity && entity.loading;
  }
);

export function getFilter(widgetId: string) {
  return createSelector([filterSelectors.selectByWidgetId(widgetId), selectEntityByWidgetId(widgetId.toString())],
    (filter: filterReducers.WidgetFilterInstance, diaryInstance: diaryReducers.DiaryWidgetInstance) => {
      if (filter && diaryInstance) {
        return filter.filter || filterUtils.getDefaultFilter();
      }
      return filterUtils.getDefaultFilter();
    });
}
function getSearchTextFilter(fields:  Array<FieldMetadata>, filterVal: string) : RuleSet {
  let filters : RuleSet = {condition: "or", rules: []};

  fields.forEach((field) => filters.rules.push({field: field.fieldName, operator: "contains", value: filterVal}));

  return filters;
}

export const getDiaryEvents = (
  view: DiaryCalendarView,
  widgetId: string,
  fieldProvider: DiaryEventFilterFieldProvider,
  widgetConfigId: number) => createSelector(
    [selectEntityByWidgetId(widgetId.toString()), widgetConfigSelectors.selectEntityByWidgetConfigId(widgetConfigId), getFilter(widgetId)],
    (entity: diaryReducers.DiaryWidgetInstance, widgetConfig: WidgetConfiguration, savedFilter: RuleSet) => {

      if (!entity || !widgetConfig) {
        return [];
      }

      fieldProvider.initialiseFields(entity.events);
      const filterStrategy = new StandardFilterStrategy<DiaryEvent>(fieldProvider);
      const filteredBySysAdminDefault: DiaryEvent[] = filterStrategy.applyFilter(entity.events, widgetConfig.filter);

      const diaryEventList: DiaryEvent[] = filterStrategy.applyFilter(filteredBySysAdminDefault, savedFilter);

      let diaryFinal: DiaryEvent[] = diaryEventList;

      if (entity.searchText != null && entity.searchText.length > 0 ) {
        const searchTextFilters = getSearchTextFilter (fieldProvider.getFilterFieldsMetadata(), entity.searchText);
        diaryFinal = filterStrategy.applyFilter(diaryEventList, searchTextFilters);
      }

      const group = groupBy(diaryFinal, 'DiaryDate', view);

      const groups: DiaryEventGroup[] = [];

      for (const property in group) {
        const items = group[property];
        const item: DiaryEventGroup = new DiaryEventGroup();
        item.DiaryEvents = items;
        item.GroupName = formatDataName(property);
        groups.push(item);
      }

      return groups;
    }
  );

function formatDataName(dataName: string): string {
  if (dataName == undefined || dataName == '' || dataName == 'null') {
    return "Undefined";
  }
  return dataName;
}


function groupBy(array: Array<any>, prop, view: DiaryCalendarView): any {

  if (!Array.isArray(array)) {
    return [];
  }

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  switch (view) {

    case DiaryCalendarView.Month: {
      return array.reduce((groups, item: DiaryEvent) => {
        const date = new Date(item.DiaryDate);
        const val = date.getDate() + ' ' + monthNames[date.getMonth()] + ' ' + date.getFullYear();
        groups[val] = groups[val] || [];
        groups[val].push(item);
        return groups;
      },
        {});
    }
    case DiaryCalendarView.Day: {
      return array.reduce((groups, item: DiaryEvent) => {
        const date = new Date(item.DiaryDate);
        const val = date.getDate() + ' ' + monthNames[date.getMonth()] + ' ' + date.getFullYear();
        groups[val] = groups[val] || [];
        groups[val].push(item);
        return groups;
      },
        {});
    }
    case DiaryCalendarView.Duodecennial: {
      return array.reduce((groups, item: DiaryEvent) => {
        const year = new Date(item.DiaryDate).getFullYear();
        groups[year] = groups[year] || [];
        groups[year].push(item);
        return groups;
      },
        {});
    }
    case DiaryCalendarView.Year: {
      return array.reduce((groups, item: DiaryEvent) => {
        const month = new Date(item.DiaryDate).getMonth();
        groups[monthNames[month]] = groups[monthNames[month]] || [];
        groups[monthNames[month]].push(item);
        return groups;
      },
        {});
    }
    default:
      {
        return [];
      }

  }
}
